import { Editor } from '@tiptap/react';
import {
  ChangeEvent,
  FunctionComponent,
  MutableRefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '../../../ConfirmationModal';
import { IconLabel, IconLabelHintDirection } from '../../../IconLabel';
import { blocksToContentConverter } from '../../../TextEditor';
import { useDocxImport } from './helpers';

import classes from './CreateCardImport.module.scss';

interface CreateCardImportProps {
  editorRef: MutableRefObject<Editor | null>;
  setFullPostTextEditorUsePrefill: (usePrefill: boolean) => void;
}

export const CreateCardImport: FunctionComponent<CreateCardImportProps> = ({
  editorRef,
  setFullPostTextEditorUsePrefill,
}) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'importDocx' });

  const { importDocx, isImporting } = useDocxImport();

  const [fileToImport, setFileToImport] = useState<File | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  const importFile = useCallback(
    async (file: File) => {
      const contentBlocks = await importDocx(file);

      if (!contentBlocks) {
        return;
      }

      editorRef.current?.commands.setContent(blocksToContentConverter(contentBlocks));

      setFullPostTextEditorUsePrefill(true);
    },
    [editorRef, importDocx, setFullPostTextEditorUsePrefill]
  );

  const onFileChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const file = target.files?.[0];

      target.value = '';

      if (!file) {
        return;
      }

      if (editorRef.current && !editorRef.current.isEmpty) {
        setFileToImport(file);
        return;
      }

      importFile(file);
    },
    [editorRef, importFile]
  );

  const onConfirmReplace = useCallback(() => {
    if (!fileToImport) {
      return;
    }

    importFile(fileToImport);
    setFileToImport(null);
  }, [fileToImport, importFile]);

  return (
    <>
      <input
        ref={fileRef}
        type={'file'}
        accept={'.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
        className={classes['create-card-import__file']}
        onChange={onFileChange}
      />

      <IconLabel
        iconId={'file'}
        iconSize={18}
        label={ctxT('label')}
        color={'#b3b3b3'}
        hoverColor={'#fff'}
        disabled={isImporting}
        onClick={() => fileRef.current?.click()}
        {...(!isMobileOnly && {
          data: ctxT('hint'),
          hintDirection: IconLabelHintDirection.bottom,
        })}
      />

      <ConfirmationModal
        isOpen={Boolean(fileToImport)}
        title={ctxT('replace-title')}
        subTitle={ctxT('replace-subtitle')}
        acceptLabel={ctxT('replace-accept')}
        onAccept={onConfirmReplace}
        onClose={() => setFileToImport(null)}
        root={'modal-editor-root'}
      />
    </>
  );
};
